import {IAGRow, IExam, IUser, QueryFilter} from "@/types/api.ts";
import ObjectChecker from "@/utils/ObjectChecker.ts";

export const API_BASE = import.meta.env.VITE_API_BASE;

export enum EndpointAction {
  LIST = "list",
  CREATE = "create",
  GET_BY_ID = "getById",
  UPDATE_BY_ID = "updateById",
  DELETE_BY_ID = "deleteById",
}
type DoById = (id: string) => string;

export type CrudEndpoints<TQuery = IAGRow> = {
  [EndpointAction.LIST]: string | ((query?: QueryFilter<TQuery> | undefined) => string);
  [EndpointAction.CREATE]: string;
  [EndpointAction.GET_BY_ID]: DoById;
  [EndpointAction.UPDATE_BY_ID]: DoById;
  [EndpointAction.DELETE_BY_ID]: DoById;
};

export type CustomActions = {
  [actionName: string]: string | ((...args: any[]) => string);
};

export type ResourceApi<TQuery = any, TCustom extends CustomActions = {}> = CrudEndpoints<TQuery> & TCustom;

function createResourceApi<
  TQuery = any,
  TCustom extends object = {}
>(
  resource: string,
  custom?: TCustom
): CrudEndpoints<TQuery> & TCustom {

  const base = API_BASE;

  const crud: CrudEndpoints<TQuery> = {
    list: (query?: QueryFilter<TQuery>) =>
      `${base}/${resource}?${toURLSearchQueryParams(query)}`,
    create: `${base}/${resource}`,
    getById: (id: string) => `${base}/${resource}/${id}`,
    updateById: (id: string) => `${base}/${resource}/${id}`,
    deleteById: (id: string) => `${base}/${resource}/${id}`,
  };

  return {
    ...crud,
    ...custom,
  } as CrudEndpoints<TQuery> & TCustom;
}

const authApi = {
  login: `${API_BASE}/auth/login`,
  loginByCodeAndPassword: `${API_BASE}/auth/login-by-code-password`,
  logout: `${API_BASE}/auth/logout`,
  refresh: `${API_BASE}/auth/refresh-token`,
  me: `${API_BASE}/auth/me`,
  googleLogin: `${API_BASE}/auth/google`,
  googleCallback: `${API_BASE}/auth/google/callback`,
  completeProfile: `${API_BASE}/auth/complete-profile`,
  update: `${API_BASE}/auth/me/update`,
}

const usersApi = createResourceApi("users", {
  me: `${API_BASE}/users/me`
})

const adminApi = {
  dashboardOverview: `${API_BASE}/admin/dashboard-overview`,
  updateUsers: `${API_BASE}/admin/update-users`,
  generateNextId: `${API_BASE}/admin/generate-next-ids`,
  forceUserToLogout: (userId: IUser['_id']) => `${API_BASE}/admin/${userId}/force-logout`
}

const gradesApi = createResourceApi("grades", {
  submit: `${API_BASE}/grades/submit-grade`,
  gradesForCurrentUser: `${API_BASE}/grades/me`,
  gradesForAllStudents: `${API_BASE}/grades`,
})

const lessonsApi = createResourceApi("lessons", {
  deleteAttendanceFromLesson: (lessonId: string, attendanceId: string) => `${API_BASE}/lessons/${lessonId}/${attendanceId}`,
  addAttendance: (lessonId: string) => `${API_BASE}/lessons/${lessonId}/add-attendance`,
  viewStudentAttendance: `${API_BASE}/lessons/student-attendance`
})

const paymentsApi = createResourceApi("payments", {
  updateMany: `${API_BASE}/payments`,
  getCurrentStudentPayments: `${API_BASE}/payments/students/me/payments`,
  getStudentPayments: (studentId: string) => `${API_BASE}/payments/students/${studentId}/payments`
})

const groupsApi = createResourceApi("groups", {
  joinStudents: (id: string) => `${API_BASE}/groups/${id}/join-students`
})

const examsApi = createResourceApi("exams", {
  addQuestions: (id: string) => `${API_BASE}/exams/${id}/questions`,
  publishExam: (id: string) => `${API_BASE}/exams/${id}/publish`,
  submitExam: (id: string) => `${API_BASE}/exams/${id}/submit`,
  getExamOrExamResult: (id: string) => `${API_BASE}/exams/${id}/exam-or-exam-result`,
  getExamResultById: (id: string) => `${API_BASE}/exams/result/${id}`,
  getExamsOverview:(query?:QueryFilter<IExam>) =>`${API_BASE}/exams/overview?${toURLSearchQueryParams(query)}`,
  updateExams:`${API_BASE}/exams/update-exams`
})
export const API_URLS = {
  auth: authApi,
  users: usersApi,
  admin: adminApi,
  grades: gradesApi,
  lessons: lessonsApi,
  payments: paymentsApi,
  groups: groupsApi,
  exams: examsApi,
  media: {
    cloudinarySignature: `${API_BASE}/cloudinary/signature`,
  },
} as const;

function toURLSearchQueryParams<T>(query?: QueryFilter<T>): string {
  if (ObjectChecker.isEmptyOrNull(query))
    return '';
  // const filter = query.filter ? CommonUtils.toQueryString(query.filter) : {}
  const searchParams = new URLSearchParams();
  searchParams.set("filter", JSON.stringify(query.filter ?? {}));
  searchParams.set("limit", String(query.limit ?? ''));
  searchParams.set("page", String(query.page ?? ''));
  return searchParams.toString();
}